<template>
  <div>
    <DriverInfo :driverSource="model.order" />
    <Card title="签约信息">
      <QS-column-box :source="signInfoSource" :list="signInfoList" :column="2" separator=":" />
    </Card>
    <Card>
      <div slot="custom-header" class="custom-header">
        <div class="custom-title">支付配置</div>
        <slot name="refresh-button"></slot>
      </div>
      <QS-column-box
        :source="withholdConfig"
        :list="getWithholdConfigList"
        :column="2"
        separator=":"
      />
      <el-table :data="deductionList" v-if="rentCategory !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI">
        <el-table-column label="支付顺序" width="100">
          <template slot-scope="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column label="支付费项" prop="feeTypeName" />
        <el-table-column label="金额" prop="feeAmount" />
      </el-table>
    </Card>
    <div class="footer">
      <el-button @click="() => emits('cancel')">取消</el-button>
      <template v-if="showOperator">
        <el-button :loading="previewLoading" @click="handlePreviewContract">预览合同</el-button>
        <el-button type="primary" @click="handleSubmitBtn">{{ getRevertBtnText }}</el-button>
      </template>
    </div>
  </div>
</template>
<script setup>
import { cancelWithhold, previewContract } from '@/api/withhold'
import { CONTRACT_STATE_MAP, SIGN_APPROVAL_STATE_MAP } from '@/pages/orders/components/contractSigningForm/config/enum'
import { CATEGORY_CODE_MAP } from '@/store/dict/rentPlanDict.js'
import { Message, MessageBox } from '@qs/ui'
import { computed, ref, toRefs, watch } from 'vue'
import DriverInfo from './DriverInfo.vue'

import { FREQUENCY_MAP, frequencyState, PERIOD_LIMIT_MAP, periodLimitState, startDateState } from '../config/enum.js'

const props = defineProps({
  model: {
    type: Object,
    default: () => ({}),
  },
  rentCategory: {
    type: [String, Number],
    default: '',
  },
})

const { model } = toRefs(props)

const emits = defineEmits(['cancel', 'submit'])

const signInfoSource = ref({
  templateName: '',
  merchantName: '',
  spName: '',
  channelTypeName: '',
})

const signInfoList = computed(() => {
  const columns = [
    {
      name: '签约主体',
      key: 'merchantName',
    },
    {
      name: '签约网约平台',
      key: 'spName',
    },
    {
      name: '支付渠道',
      key: 'channelTypeName',
    },
  ]
  // 需要签署租赁合同时才展示合同模板
  if (model.value.rentContract?.templateId) {
    columns.unshift({
      name: '租赁合同模板',
      key: 'templateName',
    })
  }
  return columns
})

// 预览合同加载标识
const previewLoading = ref(false)

const withholdConfig = ref({
  deductionFrequency: '', // 支付频率对应后端withholdConfig.type
  periodLimit: '', // 支付限额
})

const withholdConfigList = [
  {
    name: '支付频率',
    key: 'deductionFrequency',
  },
  {
    name: '支付限额',
    key: 'periodLimit',
  },
]

const deductionList = computed(() => {
  const result = []
  model.value.withholdConfig?.allFeeTypeList?.forEach((item) => {
    const { feeType, feeTypeName, feeAmount } = item
    result.push({
      feeType,
      feeTypeName,
      feeAmount,
    })
  })
  return result
})

const showOperator = computed(() => {
  const { contractState, startSignApprovalState } = model.value.withholdContract || {}
  return contractState === CONTRACT_STATE_MAP.SIGNING ||
  startSignApprovalState === SIGN_APPROVAL_STATE_MAP.PENDING_APPROVAL
})

const getWithholdConfigList = computed(() => {
  if (props.rentCategory !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI) {
    return withholdConfigList
  }
  return withholdConfigList.filter((item) => item.key !== 'deductionFrequency')
})

watch(() => props.model, (val) => {
  // 回显
  const { spName, channelTypeName, type, startDate, periodLimit } = val.withholdConfig
  signInfoSource.value.templateName = val.rentContract?.templateName || ''
  signInfoSource.value.merchantName = val.merchantName || ''
  signInfoSource.value.spName = spName || ''
  signInfoSource.value.channelTypeName = channelTypeName || ''
  let freqencyText = frequencyState[type]
  if (type === FREQUENCY_MAP.WEEK) {
    freqencyText += `/${startDateState[startDate]}`
  }
  withholdConfig.value.deductionFrequency = freqencyText
  withholdConfig.value.periodLimit = periodLimit ? `${periodLimitState[PERIOD_LIMIT_MAP.LIMIT]}/${periodLimit}元` : periodLimitState[PERIOD_LIMIT_MAP.NO_LIMIT]
}, {
  immediate: true,
})

const handlePreviewContract = async () => {
  previewLoading.value = true
  const { orderId, licenceNo, rentContract, withholdConfig: config = {} } = model.value
  const { spId, channelType } = config
  const { code, data } = await previewContract({
    orderId,
    licenceNo,
    rentContract: {
      templateId: rentContract.templateId,
    },
    withholdConfig: {
      feeTypeList: deductionList.value.map((item) => item.feeType),
      spId,
      channelType,
    },
  })
  previewLoading.value = false
  if (code === 1) {
    window.open(data, '_blank')
  }
}

// 撤回按钮的文案
const getRevertBtnText = computed(() => {
  if (model.value.withholdContract.startSignApprovalState === SIGN_APPROVAL_STATE_MAP.PENDING_APPROVAL) {
    return '撤回审批'
  }
  return '撤回签署'
})

const handleSubmitBtn = () => {
  MessageBox({
    title: '提示',
    message: `确定${getRevertBtnText.value}？`,
    showCancelButton: true,
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        instance.confirmButtonLoading = true
        cancelWithhold({
          orderId: model.value.orderId,
        }).then(({ code }) => {
          if (code === 1) {
            Message.success('撤销成功')
            emits('submit')
            emits('cancel')
            done()
          }
        }).finally(() => { instance.confirmButtonLoading = false })
      } else {
        done()
      }
    },
  }).catch(() => null)
}
</script>
<style lang="scss" scoped>
.footer {
  width: 100%;
  text-align: right;
  padding: 10px;
  position: fixed;
  right: 10px;
  bottom: 0;
  background: #F3F3F4;
  z-index: 999;
}

.custom-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .custom-title {
    line-height: 32px;
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, .9);
  }
}
</style>
